import {FC, useState, createContext, useContext, Dispatch, SetStateAction, useEffect} from 'react'
import {WithChildren} from '../../../../_metronic/helpers'
import {Session, User} from '@supabase/supabase-js'
import {supabase} from '../../../../services/supabaseClient'

export type UserWithProfile = User & {profile?: any}

export type AuthContextProps = {
  auth: Session | null
  saveAuth: (auth: Session | null) => void
  currentUser: UserWithProfile | null | undefined
  setCurrentUser: Dispatch<SetStateAction<UserWithProfile | null | undefined>>
  logout: () => Promise<void>
  loading: boolean
}

const initAuthContextPropsState: AuthContextProps = {
  auth: null,
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: async () => {},
  loading: true,
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

export const useAuth = () => {
  return useContext(AuthContext)
}

const fetchProfile = async (userId: string) => {
  const {data, error} = await supabase.from('profiles').select('*').eq('id', userId).single()
  if (error) {
    console.error('PROFILE FETCH ERROR', error)
    return null
  }
  return data
}

export const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<Session | null>(null)
  const [currentUser, setCurrentUser] = useState<UserWithProfile | null | undefined>(undefined)
  const [loading, setLoading] = useState(true)

  const loadSession = async (session: Session | null) => {
    setAuth(session)
    if (session?.user) {
      const profile = await fetchProfile(session.user.id)
      setCurrentUser({...session.user, profile})
    } else {
      setCurrentUser(null)
    }
  }

  const saveAuth = (session: Session | null) => {
    loadSession(session)
  }

  const logout = async () => {
    await supabase.auth.signOut()
    setAuth(null)
    setCurrentUser(null)
  }

  useEffect(() => {
    supabase.auth.getSession().then(async ({data}) => {
      await loadSession(data.session)
      setLoading(false)
    })

    const {data: listener} = supabase.auth.onAuthStateChange((_event, session) => {
      loadSession(session)
    })

    return () => {
      listener.subscription.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
